import { useRecoilCallback, useRecoilValue } from 'recoil';
import {
    AnnotationListController,
    useAnnotationListController,
} from '~/hooks/useAnnotationListController';
import {
    AnnotationSeriesGroups,
    useAnnotationSeriesGroups,
} from '~/hooks/useAnnotationSeriesGroups';
import { midpointMm, PointMm } from '~/domain/geometry/patientGeometry';
import { Measurement, MeasurementKind } from '~/domain/measurement/measurement';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    highlightedMeasurementState,
    measurementListState,
    measurementModeState,
} from '~/state/atoms/measurementAtoms';

/**
 * The list actions, the measurements grouped by image series with a label
 * each, so the panel can attribute values when more than one image is
 * involved, and the active mode.
 */
export interface MeasurementController
    extends AnnotationListController<Measurement>,
        AnnotationSeriesGroups {
    readonly mode: MeasurementKind;
    setMode: (mode: MeasurementKind) => void;
}

/**
 * The midpoint of a distance, the vertex of an angle. Both lie on the slice
 * the measurement was placed on.
 */
const focusOf = (measurement: Measurement): PointMm =>
    measurement.kind === 'distance'
        ? midpointMm(measurement.points[0], measurement.points[1])
        : measurement.points[1];

/**
 * The annotation-list actions for measurements, plus the active mode. The
 * engine tool owns placement. Labels come from the patient's images, so a
 * series that is on no view is still named.
 */
export function useMeasurementController(): MeasurementController {
    const list = useAnnotationListController(
        measurementListState,
        highlightedMeasurementState,
        focusOf
    );
    const seriesGroups = useAnnotationSeriesGroups(measurementListState);
    const mode = useRecoilValue(measurementModeState);

    const setMode = useRecoilCallback(
        ({ snapshot, set }) =>
            (next: MeasurementKind) => {
                // Switching kind abandons the current placement; the engine
                // tool is recreated by the tool arming when the mode atom
                // changes.
                snapshot
                    .getLoadable(viewManagerState)
                    .getValue()
                    ?.interactionContext.toolManager.cancelActivePlacementDraft();
                set(measurementModeState, next);
            },
        []
    );

    return { ...list, ...seriesGroups, mode, setMode };
}
